import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import inventoryItemService, { extractInventoryError } from '../../../../services/inventoryItemService';
import venueLocationService from '../../../../services/venueLocationService';
import wineListService from '../../../../services/wineListService';
import wineListItemService from '../../../../services/wineListItemService';
import WineSearchBox from '../../../../components/venue/WineSearchBox';
import type { WineSearchResult } from '../../../../components/venue/WineSearchBox';
import CreateWineModal from '../../../../components/venue/CreateWineModal';
import type { Wine } from '../../../../services/wineService';
import type { VenueLocation, WineList } from '../../../../types/venue';

const InventoryItemForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, defaultCurrency } = useVenue();
  const { itemId } = useParams<{ itemId?: string }>();
  const isEdit = Boolean(itemId);

  const backPath = isEdit
    ? `/admin/venues/${venueId}/inventory/${itemId}`
    : `/admin/venues/${venueId}/inventory`;

  // ── Edit mode: load existing item ─────────────────────────────────────────────
  const [initLoading, setInitLoading] = useState(isEdit);
  const [initError, setInitError] = useState<string | null>(null);

  // ── Form state ────────────────────────────────────────────────────────────────
  const [selectedWine, setSelectedWine] = useState<WineSearchResult | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalQuery, setCreateModalQuery] = useState('');
  const [locationId, setLocationId] = useState('');
  const [parLevel, setParLevel] = useState('');
  const [reorderPoint, setReorderPoint] = useState('');
  const [bottleSize, setBottleSize] = useState('');

  // ── Supporting data ───────────────────────────────────────────────────────────
  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // ── Submission ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Validation ────────────────────────────────────────────────────────────────
  const [touched, setTouched] = useState(false);
  const wineError = touched && !isEdit && !selectedWine;
  const locationError = false;

  const parLevelNum = parLevel !== '' ? Number(parLevel) : undefined;
  const reorderPointNum = reorderPoint !== '' ? Number(reorderPoint) : undefined;
  const bottleSizeNum = bottleSize !== '' ? Number(bottleSize) : undefined;
  const reorderExceedsPar =
    parLevelNum !== undefined && reorderPointNum !== undefined && reorderPointNum > parLevelNum;

  // ── Post-create: add to wine list dialog ─────────────────────────────────────
  const [postCreate, setPostCreate] = useState<{ itemId: string; wineId: string; bottleSize?: number } | null>(null);
  const [wineLists, setWineLists] = useState<WineList[]>([]);
  const [wineListsLoading, setWineListsLoading] = useState(false);
  const [selectedWineListId, setSelectedWineListId] = useState('');
  const [wineListVisibility, setWineListVisibility] = useState<'HIDDEN' | 'LIVE'>('HIDDEN');
  const [addingToList, setAddingToList] = useState(false);
  const [addToListError, setAddToListError] = useState('');

  const navigateToCreated = (createdItemId: string) =>
    navigate(`/admin/venues/${venueId}/inventory/${createdItemId}`);

  const handleSkipWineList = () => {
    if (postCreate) navigateToCreated(postCreate.itemId);
  };

  const handleAddToWineList = async () => {
    if (!postCreate || !selectedWineListId) return;
    setAddingToList(true);
    setAddToListError('');
    try {
      await wineListItemService.createItem(venueId, selectedWineListId, {
        wineId: postCreate.wineId,
        inventoryItemId: postCreate.itemId,
        visibility: wineListVisibility,
        source: 'MANUAL',
        active: true,
        ...(postCreate.bottleSize != null && { bottleSize: postCreate.bottleSize }),
        ...(defaultCurrency && { currency: defaultCurrency }),
      } as any);
      navigateToCreated(postCreate.itemId);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message ?? '';
      if (status === 409 || msg.toLowerCase().includes('exists') || msg.toLowerCase().includes('duplicate')) {
        setAddToListError('This wine is already on the selected wine list.');
      } else {
        setAddToListError('Failed to add to wine list. Please try again.');
      }
    } finally {
      setAddingToList(false);
    }
  };

  // ── Load locations ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    setLocationsLoading(true);
    venueLocationService.getLocations(venueId)
      .then(setLocations)
      .catch(() => setLocations([]))
      .finally(() => setLocationsLoading(false));
  }, [venueId]);

  // ── Load item for edit mode ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !itemId || !venueId) return;
    setInitLoading(true);
    inventoryItemService.getItem(venueId, itemId)
      .then(item => {
        if (item.parLevel != null) setParLevel(String(item.parLevel));
        if (item.reorderPoint != null) setReorderPoint(String(item.reorderPoint));
        if (item.bottleSize != null) setBottleSize(String(item.bottleSize));
        setInitError(null);
      })
      .catch(() => setInitError('Failed to load inventory item.'))
      .finally(() => setInitLoading(false));
  }, [isEdit, itemId, venueId]);

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setTouched(true);
    if (!isEdit && !selectedWine) return;
    if (reorderExceedsPar) return;

    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && itemId) {
        await inventoryItemService.updateItem(venueId, itemId, {
          ...(parLevel !== '' && { parLevel: Number(parLevel) }),
          ...(parLevel === '' && { parLevel: undefined }),
          ...(reorderPoint !== '' && { reorderPoint: Number(reorderPoint) }),
          ...(reorderPoint === '' && { reorderPoint: undefined }),
          ...(bottleSize !== '' && { bottleSize: Number(bottleSize) }),
          ...(bottleSize === '' && { bottleSize: undefined }),
        });
        navigate(backPath);
      } else {
        const created = await inventoryItemService.createItem(venueId, {
          wineId: selectedWine!._id,
          ...(locationId && { locationId }),
          ...(parLevelNum !== undefined && { parLevel: parLevelNum }),
          ...(reorderPointNum !== undefined && { reorderPoint: reorderPointNum }),
          ...(bottleSizeNum !== undefined && { bottleSize: bottleSizeNum }),
        });

        // Fetch wine lists for the post-create dialog
        setWineListsLoading(true);
        try {
          const result = await wineListService.getWineLists(venueId, 1, 100);
          setWineLists(result.data.filter(l => l.status !== 'ARCHIVED'));
        } catch {
          setWineLists([]);
        } finally {
          setWineListsLoading(false);
        }

        setPostCreate({ itemId: created._id, wineId: created.wineId, bottleSize: created.bottleSize });
      }
    } catch (err) {
      setError(extractInventoryError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state (edit init) ─────────────────────────────────────────────────
  if (initLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', alignSelf: 'flex-start', color: theme.palette.text.secondary }}>
          Back
        </Button>
        <Alert severity="error">{initError}</Alert>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxWidth: 680 }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
          >
            {isEdit ? 'Item Detail' : 'Inventory'}
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>
            {isEdit ? 'Edit Item Configuration' : 'Add Inventory Item'}
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {/* Wine — create mode only (immutable after creation) */}
          {!isEdit && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Wine</Typography>
              <WineSearchBox
                value={selectedWine}
                onChange={setSelectedWine}
                onCreateNew={query => { setCreateModalQuery(query); setCreateModalOpen(true); }}
                error={wineError}
                helperText={wineError ? 'Select a wine' : undefined}
              />
            </Box>
          )}

          {/* Location — create mode only (immutable after creation) */}
          {!isEdit && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Location <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography></Typography>
              <FormControl fullWidth size="small" error={locationError}>
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationId}
                  label="Location"
                  onChange={e => setLocationId(e.target.value)}
                  disabled={locationsLoading}
                  endAdornment={locationsLoading ? <CircularProgress size={16} sx={{ mr: 2 }} /> : null}
                >
                  <MenuItem value="">— None —</MenuItem>
                  {locations.filter(l => l.active).map(loc => (
                    <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {isEdit && (
            <Alert severity="info" icon={false}>
              Wine and location cannot be changed after creation.
              To change them, deactivate this item and create a new one.
            </Alert>
          )}

          <Divider />

          {/* Configuration */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Configuration
              {!isEdit && (
                <Typography component="span" variant="caption" color="text.secondary"> (optional)</Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Par Level"
                type="number"
                value={parLevel}
                onChange={e => setParLevel(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                helperText="Target stock level"
                sx={{ flex: 1, minWidth: 140 }}
              />
              <TextField
                size="small"
                label="Reorder Point"
                type="number"
                value={reorderPoint}
                onChange={e => setReorderPoint(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                error={reorderExceedsPar}
                helperText={reorderExceedsPar ? 'Must be ≤ par level' : 'Alert threshold'}
                sx={{ flex: 1, minWidth: 140 }}
              />
              <TextField
                size="small"
                label="Bottle Size (ml)"
                type="number"
                value={bottleSize}
                onChange={e => setBottleSize(e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                helperText="e.g. 750"
                sx={{ flex: 1, minWidth: 140 }}
              />
            </Box>
          </Box>

          {/* Opening stock note (create only) */}
          {!isEdit && (
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.07),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
              }}
            >
              <Typography variant="body2" color="info.dark">
                The item starts with a <strong>quantity of 0</strong>. Post a Manual Adjustment
                to set opening stock.
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {submitting
                ? isEdit ? 'Saving…' : 'Creating…'
                : isEdit ? 'Save Changes' : 'Create Item'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Create wine modal */}
      <CreateWineModal
        open={createModalOpen}
        initialQuery={createModalQuery}
        onCreated={(wine: Wine) => {
          setSelectedWine({
            _id: wine._id,
            name: wine.name,
            displayVintage: wine.displayVintage,
            vintageType: wine.vintageType,
            wineDisplay: {
              producerName: wine.wineName?.producer?.name,
              regionName: wine.wineName?.region?.name,
              color: wine.wineName?.color,
            },
          });
          setCreateModalOpen(false);
        }}
        onClose={() => setCreateModalOpen(false)}
      />

      {/* Post-create: add to wine list dialog */}
      <Dialog
        open={Boolean(postCreate)}
        onClose={handleSkipWineList}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Item created</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Would you like to also add this wine to a wine list?
          </Typography>

          {addToListError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddToListError('')}>
              {addToListError}
            </Alert>
          )}

          {wineListsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : wineLists.length === 0 ? (
            <Alert severity="info" icon={false}>
              No active wine lists found for this venue.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Wine list</InputLabel>
                <Select
                  value={selectedWineListId}
                  label="Wine list"
                  onChange={e => { setSelectedWineListId(e.target.value); setAddToListError(''); }}
                >
                  <MenuItem value="">— Select a wine list —</MenuItem>
                  {wineLists.map(l => (
                    <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={wineListVisibility}
                  label="Visibility"
                  onChange={e => setWineListVisibility(e.target.value as 'HIDDEN' | 'LIVE')}
                >
                  <MenuItem value="HIDDEN">Hidden</MenuItem>
                  <MenuItem value="LIVE">Live</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleSkipWineList} sx={{ textTransform: 'none' }} disabled={addingToList}>
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={handleAddToWineList}
            disabled={addingToList || !selectedWineListId || wineListsLoading}
            startIcon={addingToList ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {addingToList ? 'Adding…' : 'Add to wine list'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryItemForm;
